// src/services/creatorWorkflowService.js

import { apiRequest } from "./apiService";
import {
  norm,
  creatorKeyOf,
  normalizeWorkflowRecord,
} from "../features/creatorWorkflow/constants";

const encId = (id) => encodeURIComponent(String(id == null ? "" : id));

function normalizeWorkflowResponseList(data) {
  const items = Array.isArray(data?.items) ? data.items : [];
  return items.map(normalizeWorkflowRecord);
}

function sanitizePayload(payload = {}) {
  return {
    workflowStatus: norm(payload.workflowStatus),
    offerType: norm(payload.offerType),
    offerValue: norm(payload.offerValue),
    contentType: norm(payload.contentType),
    contentUrl: norm(payload.contentUrl),
    notes: norm(payload.notes),
    contactedAt: norm(payload.contactedAt),
    postedAt: norm(payload.postedAt),
    paidAt: norm(payload.paidAt),
  };
}

function sanitizeBulkItems(items = []) {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      const creatorKey = creatorKeyOf(
        item?.creatorKey || item?.username || item?.displayName || ""
      );

      if (!creatorKey) return null;

      return {
        creatorKey,
        ...sanitizePayload(item),
      };
    })
    .filter(Boolean);
}

/** -------------------- Creator Workflow -------------------- */

export const getCreatorWorkflows = (campaignId) => {
  const id = String(campaignId == null ? "" : campaignId).trim();
  if (!id) {
    throw new Error("campaignId is required.");
  }

  return apiRequest(`/campaigns/${encId(id)}/creator-workflows`, "GET").then((data) => ({
    items: normalizeWorkflowResponseList(data),
  }));
};

export const upsertCreatorWorkflow = (campaignId, creatorKey, payload) => {
  const id = String(campaignId == null ? "" : campaignId).trim();
  if (!id) {
    throw new Error("campaignId is required.");
  }

  const key = creatorKeyOf(creatorKey);
  if (!key) {
    throw new Error("creatorKey is required.");
  }

  if (!payload || typeof payload !== "object") {
    throw new Error("Valid payload is required to save creator workflow.");
  }

  return apiRequest(
    `/campaigns/${encId(id)}/creator-workflows/${encodeURIComponent(key)}`,
    "PUT",
    sanitizePayload(payload)
  ).then((data) => normalizeWorkflowRecord(data));
};

export const bulkUpsertCreatorWorkflows = (campaignId, items = []) => {
  const id = String(campaignId == null ? "" : campaignId).trim();
  if (!id) {
    throw new Error("campaignId is required.");
  }

  const cleanItems = sanitizeBulkItems(items);
  if (!cleanItems.length) {
    throw new Error("items[] is required.");
  }

  return apiRequest(
    `/campaigns/${encId(id)}/creator-workflows`,
    "POST",
    { items: cleanItems }
  ).then((data) => ({
    items: normalizeWorkflowResponseList(data),
  }));
};